import { useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AGREEMENT_STATUSES,
  PROCUREMENT_KINDS,
  referencePeriodFor,
  suggestResourceKey,
  type AgreementStatus,
  type CreateAgreement,
  type FrameworkAgreement,
  type Party,
  type ProcurementKind,
  type ProcurementUnit,
} from '@dashboard/contracts';
import { useCreateAgreement, useUpdateAgreement } from '../../api/queries';
import { ModalCard } from '../ui/modal-dialog';
import { MutationError } from '../platform/form-helpers';

/**
 * Recording a framework agreement.
 *
 * The award date is the field this form exists to get right. It, and not the
 * start date, decides which reference period the agreement counts in -- so it
 * is the only date with a live consequence shown beside it, and the service
 * refuses to let the agreement leave `draft` without one.
 *
 * Scope is presented as "computed, with an override" rather than as a checkbox.
 * A bare in-scope toggle would be a legal judgement made by whoever clicked
 * last with nothing recorded about why; the override demands a reason, and the
 * computed answer stays visible underneath it.
 */

interface AgreementDraft {
  key: string;
  title: string;
  reference: string;
  procurementUnitId: string;
  supplierPartyId: string;
  status: AgreementStatus;
  awardedOn: string;
  startsOn: string;
  endsOn: string;
  optionEndsOn: string;
  procurementKind: ProcurementKind;
  cpvCodes: string;
  overrideScope: boolean;
  inScopeOverride: boolean;
  scopeOverrideReason: string;
  notes: string;
}

function emptyDraft(units: ProcurementUnit[], parties: Party[]): AgreementDraft {
  return {
    key: '',
    title: '',
    reference: '',
    procurementUnitId: units[0]?.id ?? '',
    supplierPartyId: parties[0]?.id ?? '',
    status: 'draft',
    awardedOn: '',
    startsOn: '',
    endsOn: '',
    optionEndsOn: '',
    procurementKind: 'service',
    cpvCodes: '',
    overrideScope: false,
    inScopeOverride: true,
    scopeOverrideReason: '',
    notes: '',
  };
}

function draftFrom(agreement: FrameworkAgreement): AgreementDraft {
  return {
    key: agreement.key,
    title: agreement.title,
    reference: agreement.reference ?? '',
    procurementUnitId: agreement.procurementUnit.id,
    supplierPartyId: agreement.supplierParty.id,
    status: agreement.status,
    awardedOn: agreement.awardedOn ?? '',
    startsOn: agreement.startsOn ?? '',
    endsOn: agreement.endsOn ?? '',
    optionEndsOn: agreement.optionEndsOn ?? '',
    procurementKind: agreement.procurementKind,
    cpvCodes: agreement.cpvCodes.join(', '),
    overrideScope: agreement.inScopeOverride !== null,
    inScopeOverride: agreement.inScopeOverride ?? agreement.inScopeComputed,
    scopeOverrideReason: agreement.scopeOverrideReason ?? '',
    notes: agreement.notes ?? '',
  };
}

function toWrite(draft: AgreementDraft): CreateAgreement | null {
  if (draft.title.trim() === '' || draft.procurementUnitId === '' || draft.supplierPartyId === '') {
    return null;
  }
  if (draft.overrideScope && draft.scopeOverrideReason.trim() === '') return null;

  return {
    key: draft.key.trim() || suggestResourceKey(draft.title),
    title: draft.title.trim(),
    reference: draft.reference.trim() === '' ? null : draft.reference.trim(),
    procurementUnitId: draft.procurementUnitId,
    supplierPartyId: draft.supplierPartyId,
    status: draft.status,
    awardedOn: draft.awardedOn === '' ? null : draft.awardedOn,
    startsOn: draft.startsOn === '' ? null : draft.startsOn,
    endsOn: draft.endsOn === '' ? null : draft.endsOn,
    optionEndsOn: draft.optionEndsOn === '' ? null : draft.optionEndsOn,
    procurementKind: draft.procurementKind,
    cpvCodes: draft.cpvCodes
      .split(/[\s,;]+/)
      .map((code) => code.trim())
      .filter((code) => code !== ''),
    inScopeOverride: draft.overrideScope ? draft.inScopeOverride : null,
    scopeOverrideReason: draft.overrideScope ? draft.scopeOverrideReason.trim() : null,
    notes: draft.notes.trim() === '' ? null : draft.notes.trim(),
  };
}

export function AgreementDialog({
  agreement,
  units,
  parties,
  onClose,
}: {
  agreement: FrameworkAgreement | null;
  units: ProcurementUnit[];
  parties: Party[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const baseId = useId();
  const create = useCreateAgreement();
  const update = useUpdateAgreement();

  const [draft, setDraft] = useState<AgreementDraft>(() =>
    agreement ? draftFrom(agreement) : emptyDraft(units, parties),
  );
  const patch = (values: Partial<AgreementDraft>) =>
    setDraft((current) => ({ ...current, ...values }));

  const write = toWrite(draft);
  const pending = create.isPending || update.isPending;
  const period = referencePeriodFor(draft.awardedOn === '' ? null : draft.awardedOn);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!write || pending) return;
    if (agreement) {
      update.mutate(
        { id: agreement.id, ...write, version: agreement.version },
        { onSuccess: onClose },
      );
    } else {
      create.mutate(write, { onSuccess: onClose });
    }
  };

  return (
    <ModalCard
      titleId={titleId}
      title={agreement ? t('fleet.agreementForm.editTitle') : t('fleet.agreementForm.addTitle')}
      onClose={onClose}
      panelClassName="modal__panel--narrow-form"
    >
      <form className="fleet-form" onSubmit={submit}>
        <label className="editor-field" htmlFor={`${baseId}-title`}>
          <span>{t('fleet.agreements.title')}</span>
          <input
            id={`${baseId}-title`}
            className="input"
            value={draft.title}
            maxLength={200}
            autoFocus
            required
            onChange={(event) => patch({ title: event.target.value })}
          />
        </label>

        <div className="fleet-form__row">
          <label className="editor-field" htmlFor={`${baseId}-reference`}>
            <span>{t('fleet.agreements.reference')}</span>
            <input
              id={`${baseId}-reference`}
              className="input"
              value={draft.reference}
              maxLength={120}
              onChange={(event) => patch({ reference: event.target.value })}
            />
          </label>

          <label className="editor-field" htmlFor={`${baseId}-status`}>
            <span>{t('fleet.agreements.status')}</span>
            <select
              id={`${baseId}-status`}
              className="select"
              value={draft.status}
              onChange={(event) => patch({ status: event.target.value as AgreementStatus })}
            >
              {AGREEMENT_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {t(`fleet.agreementStatus.${status}`)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="fleet-form__row">
          <label className="editor-field" htmlFor={`${baseId}-unit`}>
            <span>{t('fleet.agreements.unit')}</span>
            <select
              id={`${baseId}-unit`}
              className="select"
              value={draft.procurementUnitId}
              required
              onChange={(event) => patch({ procurementUnitId: event.target.value })}
            >
              <option value="">{t('fleet.agreementForm.choose')}</option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.name}
                </option>
              ))}
            </select>
          </label>

          <label className="editor-field" htmlFor={`${baseId}-supplier`}>
            <span>{t('fleet.agreements.supplier')}</span>
            <select
              id={`${baseId}-supplier`}
              className="select"
              value={draft.supplierPartyId}
              required
              onChange={(event) => patch({ supplierPartyId: event.target.value })}
            >
              <option value="">{t('fleet.agreementForm.choose')}</option>
              {parties.map((party) => (
                <option key={party.id} value={party.id}>
                  {party.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="editor-field" htmlFor={`${baseId}-awarded`}>
          <span>{t('fleet.agreements.awardedOn')}</span>
          <input
            id={`${baseId}-awarded`}
            className="input"
            type="date"
            value={draft.awardedOn}
            onChange={(event) => patch({ awardedOn: event.target.value })}
          />
          {/* The consequence, beside the field that causes it. This date is why
              an agreement counts in one period rather than another, and a form
              that did not say so would be asking for the most consequential
              value on the row as if it were an ordinary date. */}
          <span className="cell-secondary">
            {draft.awardedOn === ''
              ? t('fleet.agreementForm.awardedHint')
              : period
                ? t('fleet.agreementForm.countsIn', { period: t(period.labelKey) })
                : t('fleet.agreementForm.countsNowhere')}
          </span>
        </label>

        <div className="fleet-form__row">
          <label className="editor-field" htmlFor={`${baseId}-starts`}>
            <span>{t('fleet.agreementForm.startsOn')}</span>
            <input
              id={`${baseId}-starts`}
              className="input"
              type="date"
              value={draft.startsOn}
              onChange={(event) => patch({ startsOn: event.target.value })}
            />
          </label>
          <label className="editor-field" htmlFor={`${baseId}-ends`}>
            <span>{t('fleet.agreementForm.endsOn')}</span>
            <input
              id={`${baseId}-ends`}
              className="input"
              type="date"
              value={draft.endsOn}
              onChange={(event) => patch({ endsOn: event.target.value })}
            />
          </label>
          <label className="editor-field" htmlFor={`${baseId}-option`}>
            <span>{t('fleet.agreementForm.optionEndsOn')}</span>
            <input
              id={`${baseId}-option`}
              className="input"
              type="date"
              value={draft.optionEndsOn}
              onChange={(event) => patch({ optionEndsOn: event.target.value })}
            />
          </label>
        </div>

        <div className="fleet-form__row">
          <label className="editor-field" htmlFor={`${baseId}-kind`}>
            <span>{t('fleet.agreementForm.procurementKind')}</span>
            <select
              id={`${baseId}-kind`}
              className="select"
              value={draft.procurementKind}
              onChange={(event) =>
                patch({ procurementKind: event.target.value as ProcurementKind })
              }
            >
              {PROCUREMENT_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {t(`fleet.procurementKind.${kind}`)}
                </option>
              ))}
            </select>
          </label>

          <label className="editor-field" htmlFor={`${baseId}-cpv`}>
            <span>{t('fleet.agreementForm.cpvCodes')}</span>
            <input
              id={`${baseId}-cpv`}
              className="input"
              value={draft.cpvCodes}
              onChange={(event) => patch({ cpvCodes: event.target.value })}
            />
            <span className="cell-secondary">
              {draft.procurementKind === 'service'
                ? t('fleet.agreementForm.cpvServiceHint')
                : t('fleet.agreementForm.cpvPurchaseHint')}
            </span>
          </label>
        </div>

        <fieldset className="editor-field editor-field--group">
          <legend>{t('fleet.agreements.scope')}</legend>
          <label className="fleet-form__check">
            <input
              type="checkbox"
              checked={draft.overrideScope}
              onChange={(event) => patch({ overrideScope: event.target.checked })}
            />
            <span>{t('fleet.agreementForm.overrideScope')}</span>
          </label>

          {draft.overrideScope ? (
            <>
              <label className="editor-field" htmlFor={`${baseId}-scope`}>
                <span>{t('fleet.agreementForm.overrideValue')}</span>
                <select
                  id={`${baseId}-scope`}
                  className="select"
                  value={draft.inScopeOverride ? 'in' : 'out'}
                  onChange={(event) => patch({ inScopeOverride: event.target.value === 'in' })}
                >
                  <option value="in">{t('fleet.agreements.inScope')}</option>
                  <option value="out">{t('fleet.agreements.outOfScope')}</option>
                </select>
              </label>
              <label className="editor-field" htmlFor={`${baseId}-reason`}>
                <span>{t('fleet.agreementForm.overrideReason')}</span>
                <textarea
                  id={`${baseId}-reason`}
                  className="input"
                  rows={2}
                  maxLength={500}
                  required
                  value={draft.scopeOverrideReason}
                  onChange={(event) => patch({ scopeOverrideReason: event.target.value })}
                />
                <span className="cell-secondary">
                  {t('fleet.agreementForm.overrideReasonHint')}
                </span>
              </label>
            </>
          ) : null}
        </fieldset>

        <MutationError error={create.error ?? update.error} />

        <div className="form-actions">
          <button type="button" className="button button--ghost" onClick={onClose}>
            {t('editor.cancel')}
          </button>
          <button type="submit" className="button button--primary" disabled={!write || pending}>
            {pending ? t('editor.saving') : t('editor.save')}
          </button>
        </div>
      </form>
    </ModalCard>
  );
}
